import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The airline table's state, held in the URL and nowhere else.
 *
 * Same rule as everywhere else here: THE URL IS THE STATE — read on render, written
 * on an action, never mirrored into React state.
 *
 * None of it reaches the server. `/airline/list` takes no filter, sort or page, so
 * every one of these narrows, orders or slices an array already in the browser. They
 * are in the URL anyway, because "the carriers off the working list, by code" is a
 * thing one administrator sends another.
 */

const KEYS = {
  query: 'q',
  working: 'working',
  sort: 'sort',
  dir: 'dir',
  page: 'page',
  rows: 'rows',
  /** The panel: an airline id, or `new` for the create form. */
  id: 'id',
} as const;

const CREATE = 'new';

export const ROWS_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_ROWS = 50;

export const AIRLINE_SORT_FIELDS = ['iataCode', 'icaoCode', 'title', 'prefix'] as const;
export type AirlineSortField = (typeof AIRLINE_SORT_FIELDS)[number];

/** By name, which is the order `/airline/list` itself returns. */
const DEFAULT_SORT: AirlineSortField = 'title';

export type Selection =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; id: number };

export interface AirlineParams {
  /** Matched against code, ICAO, prefix and both names — see `matches` in the page. */
  query: string;
  /** `''` means both — carriers on the working list and off it. */
  working: 'true' | 'false' | '';
  sort: AirlineSortField;
  dir: 'ascending' | 'descending';
  page: number;
  rows: number;
  selection: Selection;
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

export const useAirlineParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<AirlineParams>(() => {
    const sort = searchParams.get(KEYS.sort);
    const working = searchParams.get(KEYS.working);
    const rows = Number(searchParams.get(KEYS.rows));
    const page = Number(searchParams.get(KEYS.page));
    const selected = searchParams.get(KEYS.id);
    const id = Number(selected);

    return {
      query: searchParams.get(KEYS.query)?.trim() ?? '',
      working: working === 'true' || working === 'false' ? working : '',
      sort: isOneOf(AIRLINE_SORT_FIELDS, sort) ? sort : DEFAULT_SORT,
      dir: searchParams.get(KEYS.dir) === 'descending' ? 'descending' : 'ascending',
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
      rows: Number.isFinite(rows) && rows >= 1 ? Math.floor(rows) : DEFAULT_ROWS,
      selection:
        selected === CREATE
          ? { mode: 'create' }
          : Number.isInteger(id) && id > 0
            ? { mode: 'edit', id }
            : { mode: 'closed' },
    };
  }, [searchParams]);

  /**
   * Applies a change to the filters, the order or the page.
   *
   * Any change but the page itself resets to page 1: a narrower filter otherwise
   * leaves the reader on a page that no longer exists, which here means an empty
   * table under a row count that says there are matches.
   */
  const update = useCallback(
    (changes: Partial<Omit<AirlineParams, 'selection'>>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            // The field's name and its parameter's name are not the same — `query` is
            // `q` in the URL, because this query string is meant to be read.
            const param = KEYS[key as keyof typeof KEYS];

            if (value === '' || value === undefined || value === null) next.delete(param);
            else next.set(param, String(value));
          }

          if (!('page' in changes)) next.delete(KEYS.page);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Opening or closing the panel is not a filter change and leaves the page alone. */
  const select = useCallback(
    (selection: Selection) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (selection.mode === 'closed') next.delete(KEYS.id);
          else if (selection.mode === 'create') next.set(KEYS.id, CREATE);
          else next.set(KEYS.id, String(selection.id));

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const hasFilters = params.query !== '' || params.working !== '';

  const reset = useCallback(() => update({ query: '', working: '' }), [update]);

  return { params, update, select, reset, hasFilters };
};
